import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import SyncAltIcon from '@mui/icons-material/SyncAlt';

import type { DashboardCustomer } from '@models/customers';
import {
  getCustomerAvatarColorIndex,
  getCustomerDisplayName,
  getCustomerInitials,
  getCustomerOutstandingDebt,
  getCustomerSubscriptionState,
  isMigratedCustomer,
} from '@features/customers/helpers';
import { SubscriptionStates } from '@features/customers/constants';
import { BRAND_CODE_OPTIONS } from '@features/payments/constants';
import type { BrandCode } from '@models/payments';

import LoadingAnimation from '@components/LoadingAnimation';
import DateRangeChip from '@components/ui/DateRangeChip';
import DropdownChip from '@components/ui/DropdownChip';
import s from './CustomerList.module.css';

const AVATAR_CLASSES = [s.avatar0, s.avatar1, s.avatar2, s.avatar3, s.avatar4, s.avatar5];

type CustomerListProps = {
  rows: DashboardCustomer[];
  isLoading: boolean;
  isFetching: boolean;
  search: string;
  onSearchChange: (v: string) => void;
  hasDebt: boolean;
  onToggleHasDebt: () => void;
  hasActiveSubscription: boolean;
  onToggleHasActiveSubscription: () => void;
  testUser: boolean;
  onToggleTestUser: () => void;
  brandCode: BrandCode | 'all';
  onBrandCodeChange: (v: BrandCode | 'all') => void;
  fromDate: string;
  toDate: string;
  onDateRangeChange: (fromDate: string, toDate: string) => void;
  selectedId: string | null;
  onSelect: (id: string) => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
};

function CustomerList({
  rows,
  isLoading,
  isFetching,
  search,
  onSearchChange,
  hasDebt,
  onToggleHasDebt,
  hasActiveSubscription,
  onToggleHasActiveSubscription,
  testUser,
  onToggleTestUser,
  brandCode,
  onBrandCodeChange,
  fromDate,
  toDate,
  onDateRangeChange,
  selectedId,
  onSelect,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: CustomerListProps) {
  const showOverlay = isFetching && !isLoading && !isFetchingNextPage;

  return (
    <aside className={s.sidebar}>
      <header className={s.header}>
        <h1 className={s.title}>Customers</h1>
      </header>

      <div className={s.search}>
        <SearchIcon className={s.searchIcon} sx={{ fontSize: 18 }} />
        <input
          type="text"
          className={s.searchInput}
          placeholder="Search email, ID or card last digits.."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />
        {search && (
          <button
            type="button"
            className={s.searchClear}
            onClick={() => onSearchChange('')}
            aria-label="Clear search">
            <CloseIcon sx={{ fontSize: 12 }} />
          </button>
        )}
      </div>

      <div className={s.chips}>
        <button
          type="button"
          className={`${s.chip} ${hasActiveSubscription ? s.chipActive : ''}`}
          onClick={onToggleHasActiveSubscription}
          aria-pressed={hasActiveSubscription}>
          <AutorenewIcon sx={{ fontSize: 15 }} />
          Active subscription
        </button>
        <DateRangeChip fromDate={fromDate} toDate={toDate} onChange={onDateRangeChange} />

        <button
          type="button"
          className={`${s.chip} ${testUser ? s.chipActive : ''}`}
          onClick={onToggleTestUser}
          aria-pressed={testUser}>
          <ScienceOutlinedIcon sx={{ fontSize: 15 }} />
          Test users
        </button>
        <DropdownChip<BrandCode>
          label="Brand"
          value={brandCode}
          options={BRAND_CODE_OPTIONS}
          onChange={onBrandCodeChange}
          icon={<StorefrontOutlinedIcon sx={{ fontSize: 15 }} />}
        />
        <button
          type="button"
          className={`${s.chip} ${hasDebt ? s.chipDebtActive : ''}`}
          onClick={onToggleHasDebt}
          aria-pressed={hasDebt}>
          <WarningAmberIcon sx={{ fontSize: 15 }} />
          Has debt
        </button>
      </div>

      <div className={s.listWrap}>
        <div className={s.list}>
          {isLoading ? (
            <div className={s.loading}>
              <LoadingAnimation compact />
            </div>
          ) : rows.length === 0 ? (
            <div className={s.empty}>No customers match your search.</div>
          ) : (
            <>
              {rows.map((row) => {
                const name = getCustomerDisplayName(row);
                const initials = getCustomerInitials(row);
                const isActive = getCustomerSubscriptionState(row) === SubscriptionStates.ACTIVE;
                const hasOutstandingDebt = getCustomerOutstandingDebt(row) > 0;
                const isMigrated = isMigratedCustomer(row);
                const avatarClass = AVATAR_CLASSES[getCustomerAvatarColorIndex(row)];

                return (
                  <button
                    key={row.id}
                    type="button"
                    className={`${s.item} ${row.id === selectedId ? s.itemSelected : ''}`}
                    onClick={() => onSelect(row.id)}
                    aria-current={row.id === selectedId ? 'true' : undefined}>
                    <span className={`${s.avatar} ${avatarClass}`}>{initials}</span>
                    <span className={s.itemBody}>
                      <span className={s.itemName}>{name || 'N/A'}</span>
                      <span className={s.itemEmail}>{row.email}</span>
                    </span>
                    <span className={s.itemIcons}>
                      {isMigrated && (
                        <SyncAltIcon
                          className={s.iconMigrated}
                          sx={{ fontSize: 15 }}
                          titleAccess="Migrated customer"
                        />
                      )}
                      {isActive && (
                        <AutorenewIcon
                          className={s.iconActive}
                          sx={{ fontSize: 15 }}
                          titleAccess="Active subscription"
                        />
                      )}
                      {hasOutstandingDebt && (
                        <WarningAmberIcon
                          className={s.iconDebt}
                          sx={{ fontSize: 15 }}
                          titleAccess="Has debt"
                        />
                      )}
                    </span>
                  </button>
                );
              })}
              {hasNextPage && (
                <button
                  type="button"
                  className={s.loadMore}
                  onClick={onLoadMore}
                  disabled={isFetchingNextPage}>
                  {isFetchingNextPage ? 'Loading…' : 'Load more'}
                </button>
              )}
            </>
          )}
        </div>

        {showOverlay && (
          <div className={s.fetchingOverlay} aria-live="polite" aria-busy="true">
            <LoadingAnimation compact />
          </div>
        )}
      </div>
    </aside>
  );
}

export default CustomerList;
